import React, { FC, useCallback, useEffect, useState } from 'react';
import { useIntlFormat } from 'utils/intl/useIntlFormat';
import { useDataTableColumnConfigurations } from 'utils/hooks/useDataTableColumnConfigurations/useDataTableColumnConfigurations';
import { GenderModel, StatusModel } from '@utmost.co/enterprise-models';
import { DataTable, DataTableToolbar, DataTablePaginator } from '@utmost.co/utmost-design-system';
import { useGenders } from './hooks/useGenders';
import { GenderColumnType, useGenderColumns } from './getGenderColumns';
import { AddEditGenderModal } from './modals/AddEditGenderModal';
import DeleteGendersModal from './modals/DeleteGendersModal';
import { usePatchGenders } from './hooks/usePatchGenders';

const limit = 30;

type ModalState = 'ADD' | 'DELETE';

const GenderPage: FC<React.PropsWithChildren<unknown>> = () => {
  const [offset, setOffset] = useState(0);
  const [modalState, setModalState] = useState<ModalState | undefined>();
  const [gender, setGender] = useState<GenderModel | undefined>();
  const [selectedUids, setSelectedUids] = useState<string[]>([]);
  const { genders, totalCount, loading, fetch } = useGenders();
  const { columnConfiguration, patchColumnConfiguration } = useDataTableColumnConfigurations('ENGAGEMENTS_GENDER');

  const { changeStatus } = usePatchGenders();
  const format = useIntlFormat();
  useEffect(() => {
    fetch({ offset, limit });
  }, [fetch, offset]);

  const handleEditClick = (selected: GenderModel) => {
    setGender(selected);
    setModalState('ADD');
  };

  const handleDeleteClick = (uids: string[]) => {
    setSelectedUids(uids);
    setModalState('DELETE');
  };

  const refresh = useCallback(() => {
    if (offset > 0) {
      setOffset(0);
    } else {
      fetch({ offset, limit });
    }
  }, [offset, fetch]);

  const resetModalState = useCallback(() => {
    setModalState(undefined);
    setGender(undefined);
    setSelectedUids([]);
  }, []);

  const handleMutationCompleted = useCallback(() => {
    resetModalState();
    refresh();
  }, [refresh, resetModalState]);

  const handleClickToggleStatus = useCallback(
    async (uid: string, status: StatusModel) => {
      await changeStatus(uid, status);
      handleMutationCompleted();
    },
    [changeStatus, handleMutationCompleted]
  );

  const columns = useGenderColumns({
    onClickToggleStatus: handleClickToggleStatus,
    showDeleteModal: handleDeleteClick,
    onEditClick: handleEditClick
  });
  return (
    <>
      <AddEditGenderModal
        open={modalState === 'ADD'}
        modelToEdit={gender ?? null}
        onClose={resetModalState}
        onDone={handleMutationCompleted}
      />
      <DeleteGendersModal
        open={modalState === 'DELETE'}
        onClose={resetModalState}
        uids={selectedUids}
        onDone={handleMutationCompleted}
      />
      <DataTable<GenderModel, GenderColumnType, { search: string }>
        totalCount={totalCount}
        isFetching={loading}
        columns={columns}
        onColumnConfigurationChanged={patchColumnConfiguration}
        initialState={columnConfiguration}
        dataTest="genders__page__container"
        data={genders}
      >
        <DataTableToolbar
          addButtonProps={{
            onAdd: () => {
              setModalState('ADD');
            },
            disabled: false
          }}
          message={format(`table.totalCount.result`, { totalCount, pageDataCount: genders.length })}
        />

        <DataTablePaginator fetchNextPage={({ pageIndex }) => setOffset(pageIndex * limit)} />
      </DataTable>
    </>
  );
};

export default GenderPage;
